import React from 'react';

/**
 * The enrollment step of two-factor setup: the QR code, the typed-out secret
 * for authenticators that cannot use a camera, and the confirmation code entry.
 *
 * Presentational only — the caller owns the Hanko flow via useProfileSecurity
 * and passes its values straight through. Shared by the account Security panel
 * and the sign-in gate so both drive the same enrollment.
 */
export default function TwoFactorEnrollment({ enroll, code, setCode, busy, onConfirm, onCancel }) {
    if (!enroll) return null;

    return (
        <div style={styles.box}>
            <div style={styles.row}>
                {enroll.image && (
                    <img
                        src={enroll.image}
                        alt="Two-factor setup QR code"
                        width={168}
                        height={168}
                        style={styles.qr}
                    />
                )}
                <div style={styles.steps}>
                    <p style={styles.step}>
                        <strong style={styles.num}>1.</strong> Open your authenticator app
                        and scan this code.
                    </p>
                    <p style={styles.step}>
                        <strong style={styles.num}>2.</strong> Can't scan? Enter this key
                        by hand:
                    </p>
                    <code style={styles.secret}>{enroll.secret}</code>
                    <p style={styles.step}>
                        <strong style={styles.num}>3.</strong> Type the 6-digit code it
                        shows.
                    </p>
                </div>
            </div>

            <form
                onSubmit={(e) => { e.preventDefault(); onConfirm(); }}
                style={styles.form}
            >
                <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="000000"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={7}
                    autoFocus
                    style={styles.input}
                />
                <button
                    type="submit"
                    disabled={busy === 'verify'}
                    style={{ ...styles.confirmBtn, ...(busy === 'verify' ? styles.confirmBtnDisabled : {}) }}
                >
                    {busy === 'verify' ? 'Confirming…' : 'Confirm'}
                </button>
                {onCancel && (
                    <button type="button" onClick={onCancel} style={styles.cancelBtn}>
                        Cancel
                    </button>
                )}
            </form>
        </div>
    );
}

const styles = {
    box: {
        padding: '20px',
        backgroundColor: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 10,
        marginBottom: 16,
        textAlign: 'left',
    },
    row: {
        display: 'flex',
        gap: 24,
        alignItems: 'flex-start',
        flexWrap: 'wrap',
    },
    qr: {
        borderRadius: 8,
        background: '#FFFFFF',
        padding: 8,
        flexShrink: 0,
    },
    steps: {
        flex: '1 1 260px',
        minWidth: 240,
    },
    step: {
        margin: '0 0 10px',
        fontSize: 13,
        color: '#CBD5E1',
        lineHeight: '1.5',
    },
    num: {
        color: '#60A5FA',
        marginRight: 6,
    },
    secret: {
        display: 'block',
        margin: '0 0 14px',
        padding: '10px 12px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 13,
        letterSpacing: '0.08em',
        color: '#E2E8F0',
        wordBreak: 'break-all',
        backgroundColor: 'rgba(0,0,0,0.28)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 6,
        userSelect: 'all',
    },
    form: {
        display: 'flex',
        alignItems: 'center',
        marginTop: 18,
        flexWrap: 'wrap',
    },
    input: {
        width: 130,
        marginRight: 12,
        padding: '10px 14px',
        fontSize: 18,
        letterSpacing: '0.28em',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        color: '#F1F5F9',
        backgroundColor: 'rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: 8,
        outline: 'none',
    },
    confirmBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 24px',
        fontSize: 14,
        fontWeight: 600,
        color: '#FFFFFF',
        backgroundColor: '#3B82F6',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    confirmBtnDisabled: {
        backgroundColor: '#475569',
        cursor: 'not-allowed',
    },
    cancelBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '10px 18px',
        marginLeft: 10,
        fontSize: 14,
        fontWeight: 600,
        color: '#94A3B8',
        backgroundColor: 'transparent',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
};
